import { CreateRoomResponse, JoinRoomResponse, RoomData, AdminMetrics, QuickStats, DailyStats, LeaderboardResponse } from '../types';
import { ApiConnectionError, ApiHttpError, NameCollisionError } from '../utils/apiError';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE}${endpoint}`;
    let response: Response;
    try {
      response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
        },
        ...options,
      });
    } catch (error) {
      throw new ApiConnectionError(error);
    }

    if (!response.ok) {
      let body: { error?: string; assigned_name?: string } | undefined;
      try {
        body = await response.json();
      } catch {
        body = undefined;
      }
      if (response.status === 409 && body?.assigned_name) {
        throw new NameCollisionError(body.assigned_name);
      }
      throw new ApiHttpError(
        response.status,
        `API Error: ${response.status} ${response.statusText}`,
        body
      );
    }

    return response.json();
  }

  async createRoom(): Promise<CreateRoomResponse> {
    return this.request<CreateRoomResponse>('/rooms', {
      method: 'POST',
    });
  }

  async getRoom(roomId: string): Promise<RoomData> {
    return this.request<RoomData>(`/rooms/${roomId}`);
  }

  async joinRoom(roomId: string, browserFingerprint: string): Promise<JoinRoomResponse> {
    return this.request<JoinRoomResponse>(`/rooms/${roomId}/join`, {
      method: 'POST',
      body: JSON.stringify({ browser_fingerprint: browserFingerprint }),
    });
  }

  async updateAvailability(
    roomId: string,
    participant: string,
    day: string,
    hour: number,
    available: boolean,
    browserFingerprint: string,
    timezoneOffsetMinutes: number
  ): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/rooms/${roomId}/availability`, {
      method: 'POST',
      body: JSON.stringify({
        participant,
        day,
        hour,
        available,
        browser_fingerprint: browserFingerprint,
        timezone_offset_minutes: timezoneOffsetMinutes,
      }),
    });
  }

  async getAvailability(roomId: string): Promise<{ availability: any; raw: any }> {
    return this.request<{ availability: any; raw: any }>(`/rooms/${roomId}/availability`);
  }

  async renameParticipant(
    roomId: string,
    oldName: string,
    newName: string,
    browserFingerprint: string
  ): Promise<{ success: boolean; old_name: string; new_name: string }> {
    return this.request<{ success: boolean; old_name: string; new_name: string }>(
      `/rooms/${roomId}/rename`,
      {
        method: 'POST',
        body: JSON.stringify({
          old_name: oldName,
          new_name: newName,
          browser_fingerprint: browserFingerprint,
        }),
      }
    );
  }

  async updateRoomName(
    roomId: string,
    name: string,
    browserFingerprint: string
  ): Promise<{ success: boolean; name: string }> {
    return this.request<{ success: boolean; name: string }>(
      `/rooms/${roomId}/name`,
      {
        method: 'PATCH',
        body: JSON.stringify({ name, browser_fingerprint: browserFingerprint }),
      }
    );
  }

  async getLeaderboard(
    roomId: string,
    minDuration: number = 1
  ): Promise<LeaderboardResponse> {
    return this.request<LeaderboardResponse>(
      `/rooms/${roomId}/leaderboard?min_duration=${minDuration}`
    );
  }

  async getMetrics(
    periodOrDates: number | { start_date: string; end_date: string },
    adminToken: string
  ): Promise<AdminMetrics> {
    let url = '/admin/metrics';
    if (typeof periodOrDates === 'number') {
      url += `?period=${periodOrDates}`;
    } else {
      url += `?start_date=${periodOrDates.start_date}&end_date=${periodOrDates.end_date}`;
    }
    return this.request<AdminMetrics>(url, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
    });
  }

  async getQuickStats(adminToken: string): Promise<QuickStats> {
    return this.request<QuickStats>('/admin/quick-stats', {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
    });
  }

  async getDailyStats(
    daysOrDates: number | { start_date: string; end_date: string },
    adminToken: string
  ): Promise<DailyStats[]> {
    let url = '/admin/daily-stats';
    if (typeof daysOrDates === 'number') {
      url += `?days=${daysOrDates}`;
    } else {
      url += `?start_date=${daysOrDates.start_date}&end_date=${daysOrDates.end_date}`;
    }
    return this.request<DailyStats[]>(url, {
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
    });
  }
}

export const apiService = new ApiService();

// Generate a unique UUID v4
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

export function generateBrowserFingerprint(): string {
  // Check if we already have a fingerprint in localStorage
  const stored = localStorage.getItem('browser_fingerprint');
  if (stored) {
    return stored;
  }

  const sessionId = generateUUID();

  const fingerprint = [
    sessionId,
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.platform,
    navigator.hardwareConcurrency || 'unknown',
    navigator.maxTouchPoints || 0,
    Date.now()
  ].join('|');

  const encoded = btoa(fingerprint).substring(0, 64);
  localStorage.setItem('browser_fingerprint', encoded);

  return encoded;
}
